import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminApi } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';

interface TenantDetail {
    id: string;
    name: string;
    slug: string;
    region?: string;
    maxEmployees?: number;
    billingName?: string;
    billingContact?: string;
    billingRole?: string;
    taxId?: string;
    createdAt: string;
}

interface Employee {
    id: string;
    name: string;
    email: string;
    role: string;
    status: string;
}

interface Subscription {
    id: string;
    status: string;
    planId?: string;
    plan?: { name: string };
    contractMonths?: number;
    trialEndsAt?: string;
    endsAt?: string;
}

export default function CompanyDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tenant, setTenant] = useState<TenantDetail | null>(null);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [employeeCount, setEmployeeCount] = useState(0);

    const [formData, setFormData] = useState({
        maxEmployees: 10,
        region: 'US',
        billingName: '',
        billingContact: '',
        billingRole: '',
        taxId: '',
    });

    useEffect(() => {
        if (id) loadTenant(id);
    }, [id]);

    async function loadTenant(tenantId: string) {
        try {
            const data = await superAdminApi.getTenant(tenantId);
            setTenant(data.tenant);
            setEmployees(data.employees || []);
            setSubscription(data.subscription);
            setEmployeeCount(data.employeeCount || 0);

            setFormData({
                maxEmployees: data.tenant.maxEmployees || 10,
                region: data.tenant.region || 'US',
                billingName: data.tenant.billingName || '',
                billingContact: data.tenant.billingContact || '',
                billingRole: data.tenant.billingRole || '',
                taxId: data.tenant.taxId || '',
            });
        } catch (error) {
            console.error('Failed to load tenant:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleSave() {
        if (!id) return;
        setSaving(true);
        try {
            await superAdminApi.updateTenant(id, formData);
            alert('Settings saved successfully!');
        } catch (error) {
            console.error('Failed to save settings:', error);
            alert('Failed to save settings');
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return <div className="text-center py-8">Loading company details...</div>;
    }

    if (!tenant) {
        return <div className="text-center py-8 text-red-400">Company not found</div>;
    }

    return (
        <div>
            <button
                onClick={() => navigate('/super-admin/companies')}
                className="text-gray-400 hover:text-white mb-4 flex items-center gap-2"
            >
                ← Back to Companies
            </button>

            <div className="flex justify-between items-start mb-6">
                <div>
                    <h1 className="text-2xl font-bold">{tenant.name}</h1>
                    <p className="text-gray-400">{tenant.slug}</p>
                </div>
                <StatusBadge status={subscription?.status || 'trial'} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Settings Form */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Limits */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-4">Limits & Region</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Max Employees</label>
                                <input
                                    type="number"
                                    value={formData.maxEmployees}
                                    onChange={(e) => setFormData({ ...formData, maxEmployees: parseInt(e.target.value) || 10 })}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Current: {employeeCount} employees
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Region</label>
                                <select
                                    value={formData.region}
                                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                                >
                                    <option value="IN">India</option>
                                    <option value="US">United States</option>
                                    <option value="CA">Canada</option>
                                    <option value="EU">Europe</option>
                                    <option value="AU">Australia</option>
                                    <option value="SA">South America</option>
                                    <option value="SG">Singapore</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Billing */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-4">Billing Information</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Billing Name</label>
                                <input
                                    type="text"
                                    value={formData.billingName}
                                    onChange={(e) => setFormData({ ...formData, billingName: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Contact</label>
                                <input
                                    type="text"
                                    value={formData.billingContact}
                                    onChange={(e) => setFormData({ ...formData, billingContact: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Role</label>
                                <input
                                    type="text"
                                    value={formData.billingRole}
                                    onChange={(e) => setFormData({ ...formData, billingRole: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Tax ID</label>
                                <input
                                    type="text"
                                    value={formData.taxId}
                                    onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                                />
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Subscription */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">Subscription</h2>
                        {subscription ? (
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Status</span>
                                    <StatusBadge status={subscription.status} />
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Plan</span>
                                    <span>{subscription.plan?.name || '-'}</span>
                                </div>
                                {subscription.trialEndsAt && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Trial Ends</span>
                                        <span>{formatDate(subscription.trialEndsAt)}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-gray-400 text-sm">No subscription</p>
                        )}
                    </div>

                    {/* Employees Preview */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">Employees ({employeeCount})</h2>
                        <div className="space-y-2">
                            {employees.slice(0, 5).map((emp) => (
                                <div key={emp.id} className="flex justify-between text-sm">
                                    <span className="truncate">{emp.name}</span>
                                    <span className="text-gray-400">{emp.role}</span>
                                </div>
                            ))}
                            {employees.length > 5 && (
                                <p className="text-xs text-gray-500">+{employees.length - 5} more</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const styles: Record<string, string> = {
        active: 'bg-green-600 text-green-100',
        trial: 'bg-yellow-600 text-yellow-100',
        grace: 'bg-orange-600 text-orange-100',
        readonly: 'bg-red-600 text-red-100',
        expired: 'bg-gray-600 text-gray-100',
    };

    return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.trial}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
    );
}
